import React, { useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  Button,
  ClickAwayListener,
  FormControl,
  FormControlLabel,
  Paper,
  Popper,
  Checkbox,
  useTheme,
} from '@mui/material';
import {
  SquareCheckedIcon,
  SquareUncheckedIcon,
} from 'src/components/multi_select_checkbox_group';
import { ALL_SPECIMEN_TYPE_FILTER } from 'src/constants/key_constants';
import { useOrderingSpecimenTypes } from 'src/hooks/use_ordering_specimen_types';
import { SpecimenType } from 'src/types';

interface SpecimenTypeFilterProps {
  values: string[];
  onChange: (values: string[]) => void;
}

export const SpecimenTypeFilter: React.FC<SpecimenTypeFilterProps> = ({
  values,
  onChange,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const { enabledSpecimenTypes } = useOrderingSpecimenTypes();

  // Check if "All Types" is selected (default or explicitly selected)
  const isAllTypesSelected =
    values.includes(ALL_SPECIMEN_TYPE_FILTER) || values.length === 0;

  // Get the display text for the button
  const getButtonText = () => {
    if (isAllTypesSelected) {
      return 'All Orders Types';
    }

    const firstSelectedType = enabledSpecimenTypes.find(
      (type) => type.type === (values[0] as SpecimenType),
    );

    if (values.length === 1) {
      return firstSelectedType?.displayName || 'All Orders Types';
    }

    return firstSelectedType
      ? `${firstSelectedType.displayName} + ${values.length - 1}`
      : 'All Orders Types';
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAllTypesChange = (checked: boolean) => {
    if (checked) {
      onChange([ALL_SPECIMEN_TYPE_FILTER]);
    } else {
      onChange([]);
    }
  };

  const handleSpecimenTypeChange = (
    specimenType: SpecimenType,
    checked: boolean,
  ) => {
    let newValues = [...values];

    // Remove "All Types" if it's selected and we're selecting a specific type
    if (newValues.includes(ALL_SPECIMEN_TYPE_FILTER)) {
      newValues = newValues.filter((v) => v !== ALL_SPECIMEN_TYPE_FILTER);
    }

    if (checked) {
      if (!newValues.includes(specimenType)) {
        newValues.push(specimenType);
      }
    } else {
      newValues = newValues.filter((v) => v !== specimenType);
    }

    // If no specific types are selected, default to "All Types"
    if (newValues.length === 0) {
      newValues = [ALL_SPECIMEN_TYPE_FILTER];
    }

    onChange(newValues);
  };

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      <Button
        variant='outlined'
        onClick={handleClick}
        endIcon={<ArrowDropDownIcon />}
        sx={{
          height: '40px',
          minWidth: '120px',
          textTransform: 'none',
          justifyContent: 'space-between',
          borderColor: theme.palette.grey[300],
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        {getButtonText()}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement='bottom-end'
        sx={{ zIndex: 1300 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 4],
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={3}
            sx={{
              minWidth: anchorEl?.offsetWidth || 140,
              maxWidth: 250,
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <FormControl component='fieldset' sx={{ width: '100%' }}>
              <Box sx={{ margin: 0 }}>
                {/* All Types option */}
                <FormControlLabel
                  control={
                    <Checkbox
                      icon={<SquareUncheckedIcon />}
                      checkedIcon={<SquareCheckedIcon />}
                      checked={isAllTypesSelected}
                      onChange={(e) => handleAllTypesChange(e.target.checked)}
                    />
                  }
                  label='All Types'
                  sx={{
                    margin: 0,
                    padding: '8px 16px',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                    '& .MuiFormControlLabel-label': {
                      flex: 1,
                    },
                  }}
                />

                {/* Individual specimen type options */}
                {enabledSpecimenTypes.map((specimenType) => (
                  <FormControlLabel
                    key={specimenType.type}
                    control={
                      <Checkbox
                        icon={<SquareUncheckedIcon />}
                        checkedIcon={<SquareCheckedIcon />}
                        checked={
                          !isAllTypesSelected &&
                          values.includes(specimenType.type)
                        }
                        onChange={(e) =>
                          handleSpecimenTypeChange(
                            specimenType.type,
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label={specimenType.displayName}
                    sx={{
                      margin: 0,
                      padding: '8px 16px',
                      width: '100%',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                      '& .MuiFormControlLabel-label': {
                        flex: 1,
                      },
                    }}
                  />
                ))}
              </Box>
            </FormControl>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
